import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NotFound } from '../NotFound';
import clsx from 'clsx';
import type { FeatureId } from '@sequesto-os/shared';
import { useAccess } from '../../hooks/useAccess';
import { SegmentedControl, TabBar, type TabItem } from '../../components/ui/Tabs';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { Alert } from '../../components/ui/Alert';
import { Card } from '../../components/ui/Card';
import { ErrorState, LoadingState } from '../../components/ui/States';
import type { CreateTaskInput, TaskPatch } from '../../api/organizer';
import {
  useBoardTasks,
  useCreateTask,
  useDeleteTask,
  useMoveTask,
  useTaskCounts,
  useUpdateTask,
} from '../../hooks/useOrganizer';
import { TaskBoard } from './TaskBoard';
import { QuickAddTask } from './QuickAddTask';
import { TaskList } from './TaskList';
import { TaskDetailPanel } from './TaskDetailPanel';
import { CalendarView } from './CalendarView';
import { SuggestionsInbox } from './SuggestionsInbox';

/**
 * Two tabs: To-dos (with a Board / List / Calendar view toggle) and Suggested.
 * The old Board and Calendar tabs fold into To-dos; Reminders moved to Settings.
 */
type Section = 'todos' | 'suggested';

const SECTION_FEATURE: Record<Section, FeatureId> = {
  todos: 'organizer',
  suggested: 'organizer.inbox',
};

const SECTION_IDS: readonly Section[] = ['todos', 'suggested'];

function isSection(value: string | undefined): value is Section {
  return SECTION_IDS.includes(value as Section);
}

type View = 'list' | 'board' | 'calendar';
const VIEW_STORAGE_KEY = 'organizer.view';

function storedView(): View {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'board' || v === 'calendar' ? v : 'list';
  } catch {
    return 'list';
  }
}

/** Old section URLs → their new home. */
const LEGACY_SECTIONS: Record<string, string> = {
  board: '/organizer/todos',
  calendar: '/organizer/todos?view=calendar',
  reminders: '/settings',
};

export function Organizer() {
  const { section, taskId } = useParams<{ section?: string; taskId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canAccess } = useAccess();

  const canSeeCalendar = canAccess('organizer.calendar');
  const urlView = searchParams.get('view');
  const requestedView: View =
    urlView === 'board' || urlView === 'list' || urlView === 'calendar' ? urlView : storedView();
  const view: View = requestedView === 'calendar' && !canSeeCalendar ? 'list' : requestedView;

  const [onlyOverdue, setOnlyOverdue] = useState(false);
  /** The last mutation failure, kept visible until dismissed. */
  const [failure, setFailure] = useState<string | null>(null);

  const canSeeBoard = canAccess('organizer');
  const { data, isLoading, isError, error, refetch, isFetching } = useBoardTasks();
  const counts = useTaskCounts(true);

  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const deleteTask = useDeleteTask();
  const moveTask = useMoveTask();

  const tasks = data?.tasks ?? [];

  /**
   * Ids with a mutation in flight, so their cards can show as unsettled. Derived
   * from the mutations themselves rather than tracked in a second state, which
   * would need clearing on every exit path.
   */
  const pendingIds = useMemo(() => {
    const ids = new Set<string>();
    if (updateTask.isPending && updateTask.variables) ids.add(updateTask.variables.id);
    if (moveTask.isPending && moveTask.variables) ids.add(moveTask.variables.taskId);
    if (deleteTask.isPending && deleteTask.variables) ids.add(deleteTask.variables);
    return ids;
  }, [
    updateTask.isPending,
    updateTask.variables,
    moveTask.isPending,
    moveTask.variables,
    deleteTask.isPending,
    deleteTask.variables,
  ]);

  const visibleSections: TabItem<Section>[] = useMemo(() => {
    const pending = counts.data?.counts.pendingSuggestions ?? 0;
    return (
      [
        { id: 'todos' as Section, label: 'To-dos' },
        { id: 'suggested' as Section, label: pending > 0 ? `Suggested (${pending})` : 'Suggested' },
      ] satisfies TabItem<Section>[]
    ).filter((s) => canAccess(SECTION_FEATURE[s.id]));
  }, [canAccess, counts.data?.counts.pendingSuggestions]);

  const viewOptions: TabItem<View>[] = [
    { id: 'list', label: 'List' },
    { id: 'board', label: 'Board' },
    ...(canSeeCalendar ? [{ id: 'calendar' as View, label: 'Calendar' }] : []),
  ];

  const firstSection = visibleSections[0]?.id;

  if (section && LEGACY_SECTIONS[section]) {
    const target = LEGACY_SECTIONS[section]!;
    // Keep a task deep link working: /organizer/board/:id → /organizer/todos/:id
    if (taskId && section !== 'reminders') {
      return <Navigate to={`/organizer/todos/${taskId}${target.slice('/organizer/todos'.length)}`} replace />;
    }
    return <Navigate to={target} replace />;
  }

  if (section && !isSection(section)) return <NotFound />;
  if (!section || !isSection(section) || !canAccess(SECTION_FEATURE[section])) {
    return firstSection ? <Navigate to={`/organizer/${firstSection}`} replace /> : <Navigate to="/" replace />;
  }

  function changeView(next: View) {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Private mode: the URL still carries the view.
    }
    setSearchParams({ view: next }, { replace: true });
  }

  const viewQuery = `?view=${view}`;
  const openTask = (id: string) => navigate(`/organizer/todos/${id}${viewQuery}`);

  /** Report the reason a change was rolled back, never just revert silently. */
  function reportFailure(action: string, err: unknown) {
    const detail = err instanceof Error ? err.message : String(err);
    console.error(`[Organizer] ${action} failed`, err);
    setFailure(`Couldn't ${action} — ${detail}. Your board was restored.`);
  }

  function handlePatch(id: string, patch: TaskPatch) {
    setFailure(null);
    updateTask.mutate({ id, patch }, { onError: (err) => reportFailure('save that change', err) });
  }

  function handleCreate(input: CreateTaskInput) {
    setFailure(null);
    createTask.mutate(input, { onError: (err) => reportFailure('add that task', err) });
  }

  function handleDelete(id: string) {
    setFailure(null);
    deleteTask.mutate(id, { onError: (err) => reportFailure('delete that task', err) });
  }

  function handleMove(args: Parameters<typeof moveTask.mutate>[0]) {
    setFailure(null);
    moveTask.mutate(args, { onError: (err) => reportFailure('move that task', err) });
  }

  const selectedTask = taskId ? tasks.find((t) => t.id === taskId) : undefined;
  const shown = onlyOverdue
    ? tasks.filter((t) => t.dueDate !== null && t.status !== 'done' && t.dueDate < todayLocal())
    : tasks;

  return (
    <PageShell>
      <PageHeader eyebrow="Personal"
        title="Organizer"
        subtitle="Your private to-dos."
        info="To-dos picked up from your calls wait in Suggested until you accept them. Reminders and the morning digest are described in Settings › Notifications."
        meta={
          <CountsStrip
            counts={counts.data?.counts}
            isError={counts.isError}
            onlyOverdue={onlyOverdue}
            onToggleOverdue={() => setOnlyOverdue((v) => !v)}
          />
        }
      >
        {/* One row: sections on the left (TabBar), the To-dos view toggle on the right */}
        {(visibleSections.length > 1 || (section === 'todos' && canSeeBoard)) && (
          <div className="flex items-end justify-between gap-3 border-b border-border-light">
            <div className="min-w-0 flex-1 [&>div]:border-b-0">
              {visibleSections.length > 1 && (
                <TabBar tabs={visibleSections} active={section} onSelect={(next) => navigate(`/organizer/${next}`)} />
              )}
            </div>
            {section === 'todos' && canSeeBoard && (
              <div className="flex flex-none items-center gap-3 pb-1.5">
                {/* Only the first load blanks the page; a background refetch must not
                    wipe a board mid-interaction. */}
                {isFetching && !isLoading && view !== 'calendar' && (
                  <span className="text-body-xs text-text-muted">Syncing…</span>
                )}
                <SegmentedControl options={viewOptions} value={view} onChange={changeView} ariaLabel="View" />
              </div>
            )}
          </div>
        )}
      </PageHeader>

      {failure && (
        <Alert variant="error" dismissible onDismiss={() => setFailure(null)}>
          {failure}
        </Alert>
      )}

      {section === 'todos' && canSeeBoard && (
        <>
          {view === 'calendar' ? (
            <CalendarView onOpenTask={openTask} tasks={tasks} onShowList={() => changeView('list')} />
          ) : isLoading ? (
            <LoadingState rows={6} />
          ) : isError ? (
            <ErrorState title="Couldn’t load your tasks" error={error} onRetry={() => void refetch()} />
          ) : tasks.length === 0 ? (
            <EmptyBoard onCreate={handleCreate} />
          ) : view === 'board' ? (
            <TaskBoard
              tasks={shown}
              pendingIds={pendingIds}
              onPatch={handlePatch}
              onCreate={handleCreate}
              onOpen={openTask}
              onDelete={handleDelete}
              onMove={handleMove}
            />
          ) : (
            <TaskList
              tasks={shown}
              pendingIds={pendingIds}
              onPatch={handlePatch}
              onCreate={handleCreate}
              onOpen={openTask}
              onDelete={handleDelete}
            />
          )}
        </>
      )}

      {section === 'suggested' && <SuggestionsInbox />}

      <TaskDetailPanel
        task={selectedTask}
        // Back to the view it was opened from.
        onClose={() => navigate(`/organizer/${section}${section === 'todos' ? viewQuery : ''}`)}
        onPatch={handlePatch}
        onDelete={handleDelete}
      />
    </PageShell>
  );
}

/** Today as YYYY-MM-DD in the browser's timezone, not UTC. */
function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA').format(new Date());
}

interface CountsStripProps {
  counts: { open: number; overdue: number; dueToday: number; pendingSuggestions: number } | undefined;
  isError: boolean;
  onlyOverdue: boolean;
  onToggleOverdue: () => void;
}

function CountsStrip({ counts, isError, onlyOverdue, onToggleOverdue }: CountsStripProps) {
  // The sidebar badge stays quiet when counts fail; the page is where that is
  // worth saying out loud.
  if (isError) {
    return <span className="text-warning-dark">Counts are unavailable right now.</span>;
  }
  if (!counts) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        title={counts.overdue > 0 ? (onlyOverdue ? 'Show all to-dos' : 'Show only overdue') : undefined}
        onClick={onToggleOverdue}
        disabled={counts.overdue === 0}
        className={clsx(
          'rounded-full px-2.5 py-1 text-body-xs font-medium transition-colors',
          counts.overdue === 0
            ? 'bg-surface-secondary text-text-tertiary'
            : onlyOverdue
              ? 'bg-error text-text-inverse'
              : 'bg-error-light text-error-dark hover:bg-error-light/70',
        )}
      >
        {counts.overdue} overdue
      </button>
      <span className="rounded-full bg-surface-secondary px-2.5 py-1 text-body-xs text-text-secondary">
        {counts.dueToday} due today
      </span>
      <span className="rounded-full bg-surface-secondary px-2.5 py-1 text-body-xs text-text-secondary">
        {counts.open} open
      </span>
    </div>
  );
}

function EmptyBoard({ onCreate }: { onCreate: (input: CreateTaskInput) => void }) {
  return (
    <Card>
      <div className="space-y-3 py-6 text-center">
        <p className="text-body-sm text-text-secondary">No tasks yet.</p>
        <p className="text-body-xs text-text-tertiary">
          Add one below, or check Suggested for to-dos we picked up from your calls.
        </p>
        <div className="mx-auto max-w-md pt-2">
          <QuickAddTask status="todo" onCreate={onCreate} />
        </div>
      </div>
    </Card>
  );
}
